// mapTypeControl — Karte/Satellit-Umschalter. RETROFIT (2026-07-05, frontend_view-state-base): das ◄ [Typ] ►-Control
// ist jetzt der generische view/listNav.js (createListNav: label-Zentrum + Dropdown + Cycle + is-edge + Klick-außen +
// is-narrow). Map-spezifisch bleibt hier NUR: die Verdrahtung an die Karte (map.get/setMapTypeId, maptypeid_changed,
// Googles Control aus), die Varianten-Checkbox (Gelände/Beschriftungen) und der Narrow-Observer (setNarrow, wenn das
// Control nicht neben die anderen Knöpfe der oberen Reihe passt).
//
// Verantwortung: NUR UI-Verdrahtung + aktiver Zustand. Die KARTE ist die Wahrheit; aktiver Eintrag via listNav.getIndex.
import { resolveControlPosition, resolveMapType } from "../adapter/providerConstants.js";
import { str } from "../../i18n/str.js";
import { createListNav } from "../../view/listNav.js";

// Chef 7 / User 2026-10-09: the control turns narrow (vertical, listNav is-narrow) only when its wide form would not fit
// beside the other controls of the top row (e.g. the OV button) — instead of at a fixed map width (was 400px).
const ROW_GAP_PX = 10;   // free space kept between the control and its neighbours in the top row

// Pure rule (unit-tested): narrow when the wide control + the other top-row controls + the gap exceed the map width.
export function needsNarrow({ mapWidth, controlWidth, othersWidth, gap = ROW_GAP_PX }) {
    return mapWidth > 0 && controlWidth + othersWidth + gap > mapWidth;
}

// Width an element takes in its control row, margins included (Google lines up the controls of a position side by side).
function rowWidth(el) {
    const cs = getComputedStyle(el);
    return el.offsetWidth + (parseFloat(cs.marginLeft) || 0) + (parseFloat(cs.marginRight) || 0);
}

export function createMapTypeControl({ map, google, types }) {
    const outer = document.createElement('div');
    outer.className = 'topo-map-type-outer';
    // Positionierungskontext; die Varianten-Zeile hängt ABSOLUT unter dem Control (verbreitert es nicht).
    outer.style.cssText = 'margin:10px;position:relative;display:inline-flex;';

    // Aktiver Typ = Basis ODER Variante trifft die aktuelle MapTypeId (base+variant auf denselben Typ gruppiert).
    const activeIndex = () => {
        const cur = map.getMapTypeId();
        return types.findIndex((t) => resolveMapType(google, t.key) === cur || (t.variant && resolveMapType(google, t.variant.key) === cur));
    };

    // Das ◄ [Typ] ►-Control = generischer listNav (label-Zentrum). Cycle/Dropdown/is-edge/Klick-außen macht das Modul;
    // wir liefern nur den aktiven Index + reagieren auf Auswahl mit map.setMapTypeId. Kein Umlauf (wrap:false).
    const listNav = createListNav({
        items: types.map((t) => ({ key: t.key, label: t.label })),
        center: 'label',
        expandVia: 'label',
        wrap: false,
        getIndex: activeIndex,
        prevLabel: str('MAP_TYPE_PREV'),
        nextLabel: str('MAP_TYPE_NEXT'),
        centerLabel: str('MAP_TYPE_LABEL'),
        onSelect: (key) => {
            // gemerkte Variante des Zieltyps wiederherstellen (master-Feature), sonst die Basis.
            const t = types.find((x) => x.key === key);
            const target = t ? targetForType(t) : resolveMapType(google, key);
            if (target !== undefined && map.getMapTypeId() !== target) { map.setMapTypeId(target); }
        },
        // Auf-/Zuklappen: is-expanded auf den outer spiegeln (die Varianten-CSS blendet die Zeile dann aus) + Variante nachziehen.
        onToggle: (expanded) => { outer.classList.toggle('is-expanded', expanded); applyVariant(); },
    });
    outer.appendChild(listNav.element);
    // Buglist 2026-07-15 · Item 2b (aus master reingearbeitet): die CSS-Naht `.topo2-map .topo-map-type` (EIN
    // Rahmen-Schatten auf dem Control, innere .topo2-btn schattenlos) braucht die Klasse `topo-map-type` auf dem
    // Control-Frame. In der klassischen master-Version saß sie auf dem topo2-btn-listnav-Container — das ist hier
    // listNav.element. (Die listNav-Retrofit-Version dieses Feature-Branchs hatte sie beim Merge verloren.)
    listNav.element.classList.add('topo-map-type');

    // --- Varianten-Zeile (map-spezifisch) unter dem Control: Checkbox für die Variante des AKTIVEN Typs ---
    // Karte→Gelände (terrain), Satellit→Beschriftungen (hybrid). Schaltet den aktiven Typ zwischen Basis und Variante.
    const variantRow = document.createElement('label');
    variantRow.className = 'topo-map-type-variant topo2-small';   // --small-Größenkontext wie die Control-Buttons
    const variantCb = document.createElement('input');
    variantCb.type = 'checkbox';
    const variantLbl = document.createElement('span');
    variantRow.appendChild(variantCb);
    variantRow.appendChild(variantLbl);
    variantRow.addEventListener('mousedown', (e) => e.stopPropagation());   // Klick nicht als „Außen-Klick" werten
    variantCb.addEventListener('change', () => onVariantToggle(variantCb.checked));
    outer.appendChild(variantRow);

    // In-Memory-Merker pro Control (kein Persist): typeKey → war die Variante zuletzt aktiv, als wir auf dem Typ waren?
    // Beim Zurückwechseln auf den Typ wird die gemerkte Variante wiederhergestellt (Chef-Charge 8). applyVariant pflegt ihn.
    const variantMemory = {};

    // Ziel-MapTypeId beim Wechsel AUF einen Typ: gemerkte Variante wiederherstellen, sonst die Basis (aus master gemergt).
    const targetForType = (t) => resolveMapType(google, (t.variant && variantMemory[t.key]) ? t.variant.key : t.key);

    function onVariantToggle(checked) {
        const t = types[activeIndex()];
        if (!t || !t.variant) { return; }
        const target = resolveMapType(google, checked ? t.variant.key : t.key);
        if (target !== undefined && map.getMapTypeId() !== target) { map.setMapTypeId(target); }
    }

    function applyVariant() {
        const at = types[activeIndex()];
        variantRow.classList.toggle('is-hidden', !(at && at.variant));
        if (at && at.variant) {
            variantLbl.textContent = at.variant.label;
            variantCb.checked = (map.getMapTypeId() === resolveMapType(google, at.variant.key));
            variantMemory[at.key] = variantCb.checked;   // Merker pflegen: war die Variante des aktiven Typs aktiv? (für Zurückwechseln)
        }
    }

    // Karte = Wahrheit; jedes maptypeid_changed spiegelt der listNav (aktiver Typ) + die Varianten-Zeile.
    function refresh() { listNav.update(); applyVariant(); }

    map.setOptions({ mapTypeControl: false });   // Googles eingebauten Switcher auf DIESER Karte abschalten
    const listener = google.maps.event.addListener(map, 'maptypeid_changed', refresh);
    refresh();

    // Narrow only when the wide form does not fit beside the other top-row controls. The wide width is measured once
    // (briefly switching to wide if the map starts narrow — same frame, no flicker); the other controls (OV button …)
    // come from map.controls, so we only count what we put there ourselves, and are observed too, so a button that
    // appears or changes width later re-evaluates.
    let wideWidth = 0;
    const topRow = ['TOP_LEFT', 'TOP_CENTER', 'TOP_RIGHT']
        .map((p) => resolveControlPosition(google, p))
        .filter((p) => p !== undefined);
    function measureWide() {
        const wasNarrow = listNav.element.classList.contains('is-narrow');
        if (wasNarrow) { listNav.setNarrow(false); }
        wideWidth = rowWidth(outer);
        if (wasNarrow) { listNav.setNarrow(true); }
    }
    let observer = null;
    function evaluate() {
        if (!wideWidth) { measureWide(); }   // 0 while the control is not in the DOM yet
        const others = topRow.flatMap((p) => (map.controls[p] ? map.controls[p].getArray() : [])).filter((el) => el !== outer);
        others.forEach((el) => observer.observe(el));   // observing an element twice is a no-op
        const othersWidth = others.reduce((sum, el) => sum + rowWidth(el), 0);
        listNav.setNarrow(needsNarrow({ mapWidth: map.getDiv().clientWidth, controlWidth: wideWidth, othersWidth }));
    }
    if (typeof ResizeObserver !== 'undefined' && map.getDiv) {
        observer = new ResizeObserver(evaluate);
        observer.observe(map.getDiv());
    }

    return { container: outer, listener, observer, listNav };
}

// Modul-Factory für den MapUiReconciler: baut das Control, hängt es an die Position, liefert Handle { attach, destroy }.
// attach() = idempotentes Re-Push nach Controls-Clear (Tiles-Reload); destroy() entfernt Control + Listener + listNav.
export function createMapTypeControlModule({ map, google, position }) {
    const control = createMapTypeControl({
        map, google,
        types: [
            { key: 'roadmap',   label: str('MAP_TYPE_ROADMAP'),   variant: { key: 'terrain', label: str('MAP_TYPE_TERRAIN') } },   // Karte + Gelände
            { key: 'satellite', label: str('MAP_TYPE_SATELLITE'), variant: { key: 'hybrid',  label: str('MAP_TYPE_LABELS') } }    // Satellit + Beschriftungen
        ]
    });

    const pos = position !== undefined ? position : resolveControlPosition(google, 'TOP_LEFT');
    if (pos !== undefined) { map.controls[pos].push(control.container); }

    return {
        attach() {
            if (pos === undefined) { return; }
            const arr = map.controls[pos];
            if (!arr) { return; }
            for (let i = 0; i < arr.getLength(); i++) {
                if (arr.getAt(i) === control.container) { return; }
            }
            arr.push(control.container);
        },
        destroy() {
            if (control.listener) { google.maps.event.removeListener(control.listener); }
            if (control.observer) { control.observer.disconnect(); }   // Chef 7: ResizeObserver lösen
            if (control.listNav) { control.listNav.destroy(); }        // Klick-außen-Listener + interne Listener lösen
            if (pos === undefined) { return; }
            const arr = map.controls[pos];
            if (!arr) { return; }
            for (let i = arr.getLength() - 1; i >= 0; i--) {
                if (arr.getAt(i) === control.container) { arr.removeAt(i); }
            }
        }
    };
}
